export type Point = Readonly<{ x: number; y: number }>;
export type ConvexPartDefinition = Readonly<{ points: readonly Point[] }>;

export function polygonArea(points: readonly Point[]): number {
  let twiceArea = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index];
    const next = points[(index + 1) % points.length];
    if (!current || !next) continue;
    twiceArea += current.x * next.y - next.x * current.y;
  }
  return Math.abs(twiceArea) / 2;
}

export function polygonCentroid(points: readonly Point[]): Point {
  let crossSum = 0;
  let centroidX = 0;
  let centroidY = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index];
    const next = points[(index + 1) % points.length];
    if (!current || !next) continue;
    const cross = current.x * next.y - next.x * current.y;
    crossSum += cross;
    centroidX += (current.x + next.x) * cross;
    centroidY += (current.y + next.y) * cross;
  }
  if (Math.abs(crossSum) >= 1e-8) {
    return { x: centroidX / (3 * crossSum), y: centroidY / (3 * crossSum) };
  }
  if (points.length === 0) return { x: 0, y: 0 };
  return {
    x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
    y: points.reduce((sum, point) => sum + point.y, 0) / points.length,
  };
}

export function compoundCentroid(parts: readonly ConvexPartDefinition[]): Point {
  let weightedX = 0;
  let weightedY = 0;
  let totalArea = 0;
  for (const part of parts) {
    const area = polygonArea(part.points);
    const centroid = polygonCentroid(part.points);
    weightedX += centroid.x * area;
    weightedY += centroid.y * area;
    totalArea += area;
  }
  return totalArea > 0 ? { x: weightedX / totalArea, y: weightedY / totalArea } : { x: 0, y: 0 };
}

export function normalizedCompoundArea(parts: readonly ConvexPartDefinition[]): number {
  return parts.reduce((total, part) => total + polygonArea(part.points), 0);
}

export function normalizedPartsToLocal(
  parts: readonly ConvexPartDefinition[],
  centerOfMass: Point,
  baseSize: number,
): readonly (readonly Point[])[] {
  return parts.map((part) => part.points.map((point) => ({
    x: (point.x - centerOfMass.x) * baseSize,
    y: (point.y - centerOfMass.y) * baseSize,
  })));
}

export function normalizedPartsToWorld(
  parts: readonly ConvexPartDefinition[],
  centerOfMass: Point,
  baseSize: number,
  position: Point,
  angle: number,
): readonly (readonly Point[])[] {
  const cosine = Math.cos(angle);
  const sine = Math.sin(angle);
  return normalizedPartsToLocal(parts, centerOfMass, baseSize).map((part) => part.map((point) => ({
    x: position.x + point.x * cosine - point.y * sine,
    y: position.y + point.x * sine + point.y * cosine,
  })));
}

export function minimumPartThickness(
  parts: readonly ConvexPartDefinition[],
  baseSize: number,
): number {
  let minimum = Number.POSITIVE_INFINITY;
  for (const part of parts) {
    if (part.points.length === 0) continue;
    const xs = part.points.map((point) => point.x);
    const ys = part.points.map((point) => point.y);
    const width = (Math.max(...xs) - Math.min(...xs)) * baseSize;
    const height = (Math.max(...ys) - Math.min(...ys)) * baseSize;
    minimum = Math.min(minimum, width, height);
  }
  return Number.isFinite(minimum) ? minimum : baseSize * 0.2;
}
